// Formatting helpers shared by the public booking pages.

import { format } from 'date-fns';
import type {
	FeeTier,
	PublicBookingFeeSchedule,
	PublicBookingInfo,
	PublicBookingSlot,
} from '../types/publicBooking';

export const formatBookingCurrency = (amount: number): string =>
	'$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

/** "123 Main St, Brooklyn, NY 11201" — skips whatever the provider left blank. */
export const formatLocationAddress = (info: PublicBookingInfo): string =>
	[info.address, info.city, [info.state, info.zipCode].filter(Boolean).join(' ').trim()]
		.map(part => part?.trim())
		.filter(Boolean)
		.join(', ');

/** Two-letter monogram for a name, e.g. "Sarah Johnson" → "SJ". */
export const getInitials = (name: string): string =>
	name
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map(word => word.charAt(0).toUpperCase())
		.join('');

/**
 * Combines the chosen day and slot into the local `yyyy-MM-ddTHH:mm:ss` string
 * the API expects. Deliberately offset-free, matching the portal's own
 * appointment create call: the time is the salon's wall clock, so stamping the
 * customer's UTC offset would shift the booking for anyone browsing from
 * another timezone.
 */
export const buildAppointmentDateTime = (date: Date, slot: PublicBookingSlot): string => {
	const day = format(date, 'yyyy-MM-dd');
	const time = normalizeSlotTime(slot);
	return `${day}T${time}`;
};

/**
 * The same field for a waiting-list request, where no time was chosen.
 *
 * `appointmentDateTime` is the only place the requested day can travel, so it
 * is sent at midnight and the time-of-day the customer would accept rides in
 * `waitlist.preference` instead.
 */
export const buildWaitlistDateTime = (date: Date): string =>
	`${format(date, 'yyyy-MM-dd')}T00:00:00`;

/** Accepts "09:30", "09:30:00" or a "9:30 AM" label and returns "HH:mm:ss". */
const normalizeSlotTime = (slot: PublicBookingSlot): string => {
	const raw = (slot.time || slot.label || '').trim();

	const twentyFourHour = raw.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
	if (twentyFourHour) {
		const [, hours, minutes, seconds] = twentyFourHour;
		return `${hours.padStart(2, '0')}:${minutes}:${seconds ?? '00'}`;
	}

	const twelveHour = raw.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
	if (twelveHour) {
		const [, hourText, minutes, meridiem] = twelveHour;
		let hours = parseInt(hourText, 10);
		if (meridiem.toUpperCase() === 'PM' && hours !== 12) hours += 12;
		if (meridiem.toUpperCase() === 'AM' && hours === 12) hours = 0;
		return `${String(hours).padStart(2, '0')}:${minutes}:00`;
	}

	return '00:00:00';
};

/**
 * Fallback deposit schedule, banded on the service total:
 *   $1–$100 → $20 · $101–$249.99 → $35 · $250–$499.99 → $50 · $500+ → $100
 *
 * Only reached when the booking link carries no `feeSchedule` — see resolveFee.
 */
export const calcDeposit = (serviceTotal: number): number => {
	if (serviceTotal < 101) return 20;
	if (serviceTotal < 250) return 35;
	if (serviceTotal < 500) return 50;
	return 100;
};

/**
 * Fallback support fee — the non-refundable platform fee, banded the same way:
 *   $1–$100 → $3.25 · $101–$249.99 → $5.25 · $250–$499.99 → $6.50 · $500+ → $7.50
 */
export const calcSupportFee = (serviceTotal: number): number => {
	if (serviceTotal < 101) return 3.25;
	if (serviceTotal < 250) return 5.25;
	if (serviceTotal < 500) return 6.5;
	return 7.5;
};

/**
 * Picks the amount for a service total out of a server-published tier list.
 *
 * A tier applies when the total is at or below its `upTo` — the name reads as
 * "up to and including" — and a null `upTo` is the open-ended top band. The
 * list is sorted defensively rather than trusting the order it arrived in;
 * reading the bands out of order would quietly show the wrong fee. An empty or
 * missing list falls back to the constants above, so a provider with no
 * schedule configured still gets a working booking page.
 *
 * The inclusive reading means the published bands correspond to `upTo` values
 * of 100 / 249.99 / 499.99 / null. If the API instead sends 100 / 250 / 500,
 * a total of exactly $250.00 or $500.00 lands one band too low. Both are round
 * numbers a real booking can hit, so the values are worth confirming.
 */
export const resolveFee = (
	tiers: FeeTier[] | null | undefined,
	serviceTotal: number,
	fallback: (total: number) => number
): number => {
	const banded = (tiers ?? [])
		.filter(tier => tier && typeof tier.amount === 'number')
		.sort((a, b) => (a.upTo ?? Infinity) - (b.upTo ?? Infinity));
	if (banded.length === 0) return fallback(serviceTotal);

	const match = banded.find(tier => tier.upTo == null || serviceTotal <= tier.upTo);
	return (match ?? banded[banded.length - 1]).amount;
};

/**
 * Deposit and support fee for a service total, preferring the schedule the
 * booking link published over the constants compiled into this bundle.
 */
export const calcFees = (
	feeSchedule: PublicBookingFeeSchedule | null | undefined,
	serviceTotal: number
): { deposit: number; supportFee: number } => ({
	deposit: resolveFee(feeSchedule?.depositTiers, serviceTotal, calcDeposit),
	supportFee: resolveFee(feeSchedule?.supportFeeTiers, serviceTotal, calcSupportFee),
});
